import React, { useState } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Tabs from '../Tabs';
import CategoryList from './categories/CategoryList';
import CategoryForm from './categories/CategoryForm';
import CategoryFields, { SWATCHES } from './categories/CategoryFields';
import LocationsTab from './categories/LocationsTab';
import MergeCategoriesPanel, { DuplicateCategoryBanner } from './categories/MergeCategoriesPanel';
import cs from './categories/categories.module.css';
import { useCategories } from '../../data/useCategories';
import { useEvents } from '../../data/useEvents';
import { useStudents } from '../../data/useStudents';
import { COLLECTIONS } from '../../data/paths';
import { CHANNELS } from '../../domain/notifications';
import {
  allowsMakeupCredits, categoryErrors, categoryIsBillable, categoryLeadHours,
  duplicateCategoryGroups, planCategoryMerge,
} from '../../domain/categories';

// Categories & Locations, reached from the calendar's Options menu.
//
// Assembly — each list and each form renders itself.
//
// ── Edit Category ──────────────────────────────────────────────────────────
//
// MMS opens a second drawer over the first from a row's ⋮. We push the panel
// into the open drawer, with the back arrow Drawer carries for this: one
// mounted dialog rather than two, since both would listen for Escape and close
// on one press. The fields and their order are MMS's.

const TABS = ['Categories', 'Locations'];
// A new category reminds about nothing until the teacher says otherwise —
// MMS's default on its stock categories (#87) — and allows no make-up credits
// (#351), as MMS's New Category box is unticked. The stock six differ:
// `DEFAULT_CATEGORIES` in domain/categories.js says which carry a value read
// off MMS.
const emptyDraft = () => ({
  label: '', color: SWATCHES[0], billable: true, allowMakeupCredits: false,
  reminderLeadHours: { email: null, sms: null },
});

// A stored category as the editor's draft. The leads go through
// `categoryLeadHours` rather than being read off the document, so a category
// without the field, or with a value nobody should trust, opens on "No
// Reminders".
const draftOf = (c) => ({
  id: c.id,
  label: c.label || '',
  color: c.color || SWATCHES[0],
  billable: categoryIsBillable(c),
  // Through `allowsMakeupCredits` for the same reason: a missing or
  // non-boolean field opens on off (domain/categories.js).
  allowMakeupCredits: allowsMakeupCredits(c),
  reminderLeadHours: {
    email: categoryLeadHours(c, CHANNELS.email),
    sms: categoryLeadHours(c, CHANNELS.sms),
  },
});

export default function CategoriesDrawer({ open, onClose }) {
  const {
    categories, locations, locationsLoaded, locationsFailed, saveCategory, removeCategory, saveLocation, removeLocation,
    applyCategoryMerge,
  } = useCategories();
  const { relabelCategory, series = [], overrides = [] } = useEvents();
  // The roster carries the same denormalised label the calendar does (#110).
  // Aliased because both providers export the verb under the same name: one
  // operation over two collections, and a rename that reached one and not the
  // other would put a studio's lessons and its students at odds on screen.
  const { relabelCategory: relabelStudentCategory, students = [] } = useStudents();

  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState(emptyDraft);
  const [editing, setEditing] = useState(null);
  const [locDraft, setLocDraft] = useState('');
  const [busy, setBusy] = useState(false);
  // What the last Add or Save refused, one map per surface: the Add block and
  // the Edit panel are two drafts, and the Edit panel is reached from a list
  // the Add block sits under, so one map would carry an error across.
  const [addErrors, setAddErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  // Which duplicate key's merge panel is open (#142), or null; which member the
  // teacher nominated to keep, or null; and what the last attempt refused with.
  //
  // A key rather than the group, so the group and the plan are re-derived from
  // the live list on every render: a panel left open while another device's
  // write lands is re-costed against what the studio holds now.
  const [mergingKey, setMergingKey] = useState(null);
  const [mergeKeepId, setMergeKeepId] = useState(null);
  const [mergeRefusal, setMergeRefusal] = useState('');
  // What the last Delete refused with, or '' (#631).
  const [removeRefusal, setRemoveRefusal] = useState('');
  const removeCategoryFromList = (id) => run(async () => {
    setRemoveRefusal((await removeCategory(id)) || '');
  });

  // ── The duplicate #129 cannot prevent, offered for repair (#142) ──────────
  //
  // `duplicateCategoryGroups` is cheap and the list is capped at 200, so it is
  // asked on every render rather than memoised. A studio with no duplicates
  // (two devices adding one name while both offline) renders nothing extra.
  const duplicates = duplicateCategoryGroups(categories);
  const merging = mergingKey != null;
  const mergingGroup = merging
    ? duplicates.find((g) => g.key === mergingKey) || null
    : null;
  // The panel stays open when the group goes — another device's delete can
  // arrive while the teacher is in it — and says what happened, while the
  // footer refuses.
  const mergeStale = merging && !mergingGroup;

  // Every document that names an event category, in `planCategoryMerge`'s row
  // shape: a series, an occurrence override and a student all carry
  // `categoryId`/`categoryLabel`, the same set `saveEdits` writes through its
  // relabels. A collection missing here would be left pointing at a deleted
  // category, so the two lists must agree.
  const rowsOf = (docs, collection) => docs
    .filter((d) => d?.id && d?.categoryId)
    .map((d) => ({
      collection, id: d.id, categoryId: d.categoryId, categoryLabel: d.categoryLabel ?? null,
    }));
  const mergeRows = mergingGroup
    ? [
      ...rowsOf(series, COLLECTIONS.eventSeries),
      ...rowsOf(overrides, COLLECTIONS.occurrences),
      ...rowsOf(students, COLLECTIONS.students),
    ]
    : [];

  const mergePlan = mergingGroup && mergeKeepId
    ? planCategoryMerge({
      categories, rows: mergeRows, key: mergingGroup.key, keepId: mergeKeepId,
    })
    : null;
  // Every reason this merge cannot be committed as planned. Not "the teacher
  // has chosen nothing", which is a sentence on submit rather than a dead
  // button (#116).
  const mergeUnavailable = mergeStale || (mergePlan != null && mergePlan.verdict !== 'merge');

  const openMerging = (key) => {
    setMergingKey(key);
    setMergeKeepId(null);
    setMergeRefusal('');
  };
  // Leaving the panel drops the choice and the refusal with it, for
  // `closeEditing`'s reason.
  const closeMerging = () => {
    setMergingKey(null);
    setMergeKeepId(null);
    setMergeRefusal('');
  };

  // Choosing nothing is refused with a sentence rather than a greyed button
  // (#116). A stale group is not something the teacher can fix here, so it
  // greys the button and the panel says why.
  const mergeCategories = () => {
    if (!mergingGroup || mergeUnavailable) return undefined;
    if (!mergeKeepId) { setMergeRefusal('Choose which category to keep.'); return undefined; }
    setMergeRefusal('');
    return run(async () => {
      await applyCategoryMerge(mergePlan);
      closeMerging();
    });
  };

  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  // A field being fixed stops being red as it is fixed, as `patch` does in
  // hooks/useExpenseEntryForm.js. Per field, not the whole map: a drawer
  // refused for both a duplicate name and an out-of-range lead keeps the lead's
  // message when the name is retyped.
  const surviving = (prev, before, after) => {
    if (!Object.keys(prev).length) return prev;
    const next = { ...prev };
    if (after.label !== before?.label) delete next.label;
    const was = before?.reminderLeadHours || {};
    const now = after.reminderLeadHours || {};
    if (was.email !== now.email) delete next.emailLead;
    if (was.sms !== now.sms) delete next.smsLead;
    return next;
  };

  const changeDraft = (next) => {
    setDraft(next);
    setAddErrors((prev) => surviving(prev, draft, next));
  };
  const changeEditing = (next) => {
    setEditing(next);
    setEditErrors((prev) => surviving(prev, editing, next));
  };

  // Add refuses a name the studio already answers to (#129). Save stays
  // reachable and the refusal arrives on submit (#116); the empty name still
  // greys the button (`categoryErrors` says why).
  const addCategory = () => {
    if (!draft.label.trim()) return undefined;
    const refusals = categoryErrors(draft, categories);
    setAddErrors(refusals);
    if (Object.keys(refusals).length) return undefined;
    return run(async () => {
      // A refusal is a sentence (#631: the built-ins could not be confirmed
      // with the server); nothing was written, and the draft stays.
      const refused = await saveCategory(draft);
      if (refused) { setAddErrors({ label: refused }); return; }
      setDraft(emptyDraft());
      setAddErrors({});
    });
  };

  const addLocation = () => {
    if (!locDraft.trim()) return;
    return run(async () => { await saveLocation({ label: locDraft }); setLocDraft(''); });
  };

  // Save the category, then carry its events and its students with it.
  //
  // Three writes, over the providers that own them: the category record first,
  // because it is the record of what the teacher asked for, then the events
  // and the students repair from it. Each relabel writes only the documents
  // whose stored label differs, so a save without a rename costs nothing and
  // saving again finishes an interrupted rename. `relabelCategory` in
  // data/useEvents.jsx says why the events follow at all; a student carries
  // `categoryLabel` for the Lesson Settings card, the Category column and the
  // report runner (#110).
  //
  // The duplicate-name refusal (#129) excludes the category being edited:
  // renaming "Lesson" to "Lesson", or changing only its colour, is no clash.
  const saveEdits = () => {
    if (!editing || !editing.label.trim()) return undefined;
    const label = editing.label.trim();
    const refusals = categoryErrors({ ...editing, label }, categories);
    setEditErrors(refusals);
    if (Object.keys(refusals).length) return undefined;
    return run(async () => {
      const refused = await saveCategory({ ...editing, label });
      if (refused) { setEditErrors({ label: refused }); return; }
      await relabelCategory({ categoryId: editing.id, label });
      await relabelStudentCategory({ categoryId: editing.id, label });
      setEditing(null);
      setEditErrors({});
    });
  };

  // Leaving the Edit panel drops what it refused with it: the panel is rebuilt
  // from the record on the next Edit.
  const closeEditing = () => { setEditing(null); setEditErrors({}); };

  // Three panels in one drawer; the merge panel is pushed in as Edit Category
  // is (see this file's header).
  let title = 'Categories & Locations';
  if (editing) title = 'Edit Category';
  else if (merging) title = 'Merge Categories';

  let onBack = null;
  if (editing) onBack = closeEditing;
  else if (merging) onBack = closeMerging;

  let footer = (
    <>
      <FooterSpacer />
      <Button onClick={onClose}>Done</Button>
    </>
  );
  if (editing) {
    footer = (
      <>
        <FooterSpacer />
        <Button variant="secondary" onClick={closeEditing}>Cancel</Button>
        <Button onClick={saveEdits} loading={busy} disabled={!editing.label.trim()}>Save</Button>
      </>
    );
  } else if (merging) {
    footer = (
      <>
        <FooterSpacer />
        <Button variant="secondary" onClick={closeMerging}>Cancel</Button>
        {/* Reachable with nothing chosen — that refusal is a sentence. Greyed
            only when the plan says there is nothing left to merge, which the
            teacher cannot fix from this panel. */}
        <Button
          onClick={mergeCategories}
          loading={busy}
          disabled={mergeUnavailable}
        >
          Merge Categories
        </Button>
      </>
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      onBack={onBack}
      width={620}
      footer={footer}
    >
      {editing ? (
        <CategoryFields
          draft={editing}
          onChange={changeEditing}
          idPrefix={`edit-category-${editing.id}`}
          disabled={busy}
          errors={editErrors}
        />
      ) : merging ? (
        <MergeCategoriesPanel
          group={mergingGroup}
          plan={mergePlan}
          stale={mergeStale}
          keepId={mergeKeepId}
          onChoose={(id) => { setMergeKeepId(id); setMergeRefusal(''); }}
          refusal={mergeRefusal}
          busy={busy}
        />
      ) : (
        <>
          <Tabs tabs={TABS} current={tab} onChange={setTab} />

          {tab === 0 ? (
            <>
              <DuplicateCategoryBanner groups={duplicates} onMerge={openMerging} busy={busy} />
              {removeRefusal ? <p className={cs.mergeError} role="status">{removeRefusal}</p> : null}
              <CategoryList
                categories={categories}
                onRemove={removeCategoryFromList}
                onEdit={(c) => setEditing(draftOf(c))}
                busy={busy}
              />
              <CategoryForm
                draft={draft}
                onChange={changeDraft}
                onAdd={addCategory}
                busy={busy}
                errors={addErrors}
              />
            </>
          ) : (
            <LocationsTab
              locations={locations}
              loaded={locationsLoaded}
              failed={locationsFailed}
              onRemove={removeLocation}
              draft={locDraft}
              onDraftChange={setLocDraft}
              onAdd={addLocation}
              busy={busy}
            />
          )}
        </>
      )}
    </Drawer>
  );
}
